/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-props-no-spreading */
import CloseIcon from '@mui/icons-material/Close';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Alert, Box, Button, Divider, Link, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { API_S3_UPLOAD_INFO_URL, API_S3_VIDEO_UPLOAD_INFO_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { snakeCaseKeys } from '../../util/object';
import posthog from '../../util/posthog';
import { postRequestJSON } from '../../util/services';
import S3Upload from '../forms/S3Upload';
import Select from '../forms/Select';

interface BulkEditFormInputs {
  object: string;
  links: any;
  path: any;
}

export const defaultValues: BulkEditFormInputs = {
  object: 'campaign',
  links: null,
  path: null,
};

const normalizePayload = (form) => snakeCaseKeys(form);

interface BulkEditProps {
  onClose?: Function;
}

export const typeArray = [
  {
    label: 'Insertion Order',
    value: 'insertionOrder',
    apiPath: '/admin/insertion_orders/bulk_update',
    template: '/ventura-uploads/production/template_files/bulk_io_template.csv',
  },
  {
    label: 'Campaign',
    value: 'campaign',
    apiPath: '/admin/campaigns/bulk_update',
    template: '/ventura-uploads/production/template_files/bulk_campaign_template.csv',
  },
  {
    label: 'Display Creative',
    value: 'creative',
    apiPath: '/admin/creatives/bulk_update',
    template: '/ventura-uploads/production/template_files/bulk_creative_template.csv',
  },
  {
    label: 'Video Creative',
    value: 'videoCreative',
    apiPath: '/admin/creatives/bulk_update',
    template: '/ventura-uploads/production/template_files/bulk_video_creative_template.csv',
  },
  {
    label: 'Targeted Creative',
    value: 'targetedCreative',
    apiPath: '/admin/targeted_creatives/bulk_update',
    template: '/ventura-uploads/production/template_files/bulk_targeted_creative_template.csv',
  },
];

function BulkEdit({ onClose }: BulkEditProps) {
  const { apiHost } = useConfig();
  const { constants } = useConstants();
  const [isMultipleUpload, setMultipleUpload] = useState(false);
  const [isReadyForSubmit, setReadyForSubmit] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    severity: undefined,
    message: undefined,
  });

  const S3_DIRECT_UPLOAD_URL = get(constants, 'S3_DIRECT_UPLOAD_URL');
  const STAGE = get(constants, 'RAILS.STAGE');

  const form = useForm<BulkEditFormInputs>({
    defaultValues: {
      ...defaultValues,
    },
  });

  const { watch, setValue, handleSubmit } = form;

  const object = watch('object');

  const onS3UploadSuccess = (linksParam) => {
    posthog.capture('bulk_edit_file_uploaded', { object_type: object });
    setValue('links', linksParam);
    setUploadStatus({
      severity: 'info',
      message: 'The CSV was successfully uploaded. You may now submit the bulk edit.',
    });
    setReadyForSubmit(true);
  };

  useEffect(() => {
    setMultipleUpload(object === 'creative' || object === 'videoCreative');
  }, [object]);

  const onSubmit = async (fields) => {
    if (!isEmpty(fields)) {
      const payload = normalizePayload(fields);

      const { apiPath } = typeArray.find((item) => item.value === object);
      const { head } = await postRequestJSON(apiHost, apiPath, payload);

      if (head === 'ok') {
        setReadyForSubmit(false);
        setUploadStatus({
          severity: 'success',
          message: 'Bulk edit successfully submitted.',
        });
        posthog.capture('bulk_edit_submitted', { object_type: object });
      } else {
        posthog.captureException(new Error(`bulk_edit_failed: ${object}`));
      }
    }
  };

  const generateKey = () => `${STAGE}/bulk_edits/${new Date().getTime()}`;

  return (
    <FormProvider {...form}>
      <Box p={1}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h5">Bulk Edit</Typography>
          <Button variant="text" onClick={() => onClose()}>
            <CloseIcon />
          </Button>
        </Stack>
        <Stack mt={4} alignItems="center" sx={{ width: '100%' }} spacing={2}>
          {uploadStatus.severity && uploadStatus.message && (
            <Alert severity={uploadStatus.severity}>{uploadStatus.message}</Alert>
          )}
          <Typography>
            Please fill out the CSV file using one of the templates below. Choose the type you want to bulk edit in the
            dropdown, upload the CSV from your local, and then hit &quot;Submit&quot; to execute the bulk edit.
          </Typography>
          <Stack alignItems="center" spacing={2}>
            <S3Upload
              name="path"
              s3InfoUrl={isMultipleUpload ? API_S3_VIDEO_UPLOAD_INFO_URL : API_S3_UPLOAD_INFO_URL}
              generateKey={generateKey}
              accept={isMultipleUpload ? '.csv,.png,.gif,.jpg,.mp4' : '.csv'}
              multiple={isMultipleUpload}
              onSuccess={onS3UploadSuccess}
            />
            <Stack direction="row" spacing={2}>
              <Select label="Choose a type" name="object" defaultValue={defaultValues.object} options={typeArray} />{' '}
              <Button disabled={!isReadyForSubmit} onClick={handleSubmit(onSubmit)}>
                Submit
              </Button>
            </Stack>
          </Stack>
          <Divider sx={{ width: '100%' }} />
          <Stack alignItems="center" spacing={2}>
            <Box>
              <Typography>Download Templates</Typography>
            </Box>
            <Box sx={{ width: '66%', flexWrap: 'wrap', justifyContent: 'center' }} display="flex">
              {typeArray.map((item) => (
                <Box mx={2} my={1} display="flex">
                  <Stack spacing={1} direction="row">
                    <SaveAltIcon />
                    <Typography variant="subtitle2">
                      <Link onClick={() => window.open(`${S3_DIRECT_UPLOAD_URL}${item.template}`, '_blank')}>
                        {item.label}
                      </Link>
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  );
}

export default BulkEdit;
